import { classNames } from '@/utils/class-names';
import '../style.css';
import { useRef, useState } from 'react';
import Tooltip from '../tooltip';

export interface Answer {
  __typename: 'SurveyAnswerEntry';
  key: string;
  answer: {
    __typename: 'SurveyAnswer';
    label: string;
    value: number;
    percentage: number;
    color: string;
  };
}

interface InteractiveBarSegmentProps {
  answer: Answer['answer'];
  width: string;
  color: string;
  hoverKey: string;
  hoveredSection: string | null;
  setHoveredSection: (value: string | null) => void;
}

function InteractiveBarSegment({
  answer,
  width,
  color,
  hoverKey,
  hoveredSection,
  setHoveredSection,
}: InteractiveBarSegmentProps) {
  return (
    <div style={{ width }} className="relative h-full transition-all duration-300 ease-in-out">
      <div
        className={`relative flex h-full w-full items-center justify-center  transition-shadow hover:shadow-md`}
        style={{ backgroundColor: color }}
        onMouseEnter={() => setHoveredSection(hoverKey)}
        onMouseLeave={() => setHoveredSection(null)}
        title={`${answer.label}: ${answer.percentage}%`}>
        {answer.percentage >= 10 && (
          <p className="text-center text-sh-11-20 font-bold text-gray-700">{answer.percentage}%</p>
        )}
      </div>
      <Tooltip
        label={answer.label}
        percentage={answer.percentage}
        visible={hoveredSection === hoverKey}
        value={answer.value}
      />
    </div>
  );
}

interface NeutralSegmentProps {
  side: 'left' | 'right';
  width: number;
  label: string;
  value: number;
  color: string;
  percentage: number;
  hoveredSection: string | null;
  setHoveredSection: (value: string | null) => void;
  ownRef: React.RefObject<HTMLDivElement>;
  siblingRef: React.RefObject<HTMLDivElement>;
}

function NeutralSegment({
  side,
  width,
  label,
  percentage,
  value,
  color,
  hoveredSection,
  setHoveredSection,
  ownRef,
  siblingRef,
}: NeutralSegmentProps) {
  const hoverKey = side === 'left' ? 'neutral1' : 'neutral2';
  return (
    <div
      ref={ownRef}
      className=" hover:shadow-d relative transition-all duration-300 ease-in-out"
      style={{ width: `${width}%`, backgroundColor: color }}
      onMouseEnter={() => {
        setHoveredSection('neutral1');
        if (siblingRef.current) {
          siblingRef.current.classList.add('shadow-md');
        }
      }}
      onMouseLeave={() => {
        setHoveredSection(null);
        if (siblingRef.current) {
          siblingRef.current.classList.remove('shadow-md');
        }
      }}>
      <div className="w-full" title={`${label}: ${percentage}%`} />
      <Tooltip
        top={20}
        className="!left-[62%]"
        label={label}
        percentage={percentage}
        visible={hoveredSection === hoverKey && hoverKey == 'neutral1'}
        value={value}
      />
    </div>
  );
}

export default function LikertScale({ answers }: { answers: Answer[] }) {
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const neutral1Ref = useRef<HTMLDivElement>(null);
  const neutral2Ref = useRef<HTMLDivElement>(null);

  if (!answers) return null;

  // Destructure answers (note the order matters for layout)
  const [negative2, negative1, neutral, positive1, positive2] = answers;

  const neutralHalfWidth = neutral.answer.percentage / 2;

  return (
    <div className={classNames('chart relative flex justify-center')}>
      <div className=" flex h-[22px] w-full">
        <div className="flex w-[50%] justify-end">
          <InteractiveBarSegment
            answer={negative2.answer}
            width={`${negative2.answer.percentage}%`}
            color={`${negative2.answer.color}`}
            hoverKey="negative2"
            hoveredSection={hoveredSection}
            setHoveredSection={setHoveredSection}
          />
          <InteractiveBarSegment
            answer={negative1.answer}
            width={`${negative1.answer.percentage}%`}
            color={`${negative1.answer.color}`}
            hoverKey="negative1"
            hoveredSection={hoveredSection}
            setHoveredSection={setHoveredSection}
          />
          <NeutralSegment
            side="left"
            value={neutral.answer.value}
            width={neutralHalfWidth}
            label={neutral.answer.label}
            percentage={neutral.answer.percentage}
            hoveredSection={hoveredSection}
            color={`${neutral.answer.color}`}
            setHoveredSection={setHoveredSection}
            ownRef={neutral1Ref}
            siblingRef={neutral2Ref}
          />
        </div>
        <div className="flex w-[50%] justify-start">
          <NeutralSegment
            side="right"
            value={neutral.answer.value}
            width={neutralHalfWidth}
            color={`${neutral.answer.color}`}
            label={neutral.answer.label}
            percentage={neutral.answer.percentage}
            hoveredSection={hoveredSection}
            setHoveredSection={setHoveredSection}
            ownRef={neutral2Ref}
            siblingRef={neutral1Ref}
          />
          <InteractiveBarSegment
            answer={positive1.answer}
            width={`${positive1.answer.percentage}%`}
            color={`${positive1.answer.color}`}
            hoverKey="positive1"
            hoveredSection={hoveredSection}
            setHoveredSection={setHoveredSection}
          />
          <InteractiveBarSegment
            answer={positive2.answer}
            width={`${positive2.answer.percentage}%`}
            color={`${positive2.answer.color}`}
            hoverKey="positive2"
            hoveredSection={hoveredSection}
            setHoveredSection={setHoveredSection}
          />
        </div>
      </div>
      <div
        onMouseEnter={() => {
          setHoveredSection('neutral1');
          if (neutral1Ref.current) {
            neutral1Ref.current.classList.add('shadow-md');
          }
          if (neutral2Ref.current) {
            neutral2Ref.current.classList.add('shadow-md');
          }
        }}
        onMouseLeave={() => {
          setHoveredSection(null);
          if (neutral1Ref.current) {
            neutral1Ref.current.classList.remove('shadow-md');
          }
          if (neutral2Ref.current) {
            neutral2Ref.current.classList.remove('shadow-md');
          }
        }}
        className="absolute mt-[1px] flex h-[22px] justify-center text-sh-11-20"
        style={{ rotate: neutral.answer.percentage < 5 ? '90deg' : '0deg' }}>
        {neutral.answer.percentage >= 10 && (
          <p className="text-sh-11-20 font-bold text-gray-700">{neutral.answer.percentage}%</p>
        )}
      </div>
    </div>
  );
}
